import React, { useEffect, useState } from "react";

import { BaseDatasetDTO } from "api/model/dataset/BaseDatasetDTO";
import {
  AggregationType,
  ChartData,
  ChartType,
} from "components/dashboard/dataset-window/models/types";
import { createIStatsRows, createPieData } from "components/dashboard/dataset-window/utils/utils";
import { T } from "services/lang/LanguageService";
import { checkIfArraysLengthIsSame } from "utils/utils";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import BasicChart from "components/dashboard/charts/BasicChart";
import { configBoxPlotData } from "components/dashboard/charts/ChartConfig/configBoxPlotData";
import { configHistogramData } from "components/dashboard/charts/ChartConfig/configHistogramData";
import { configLineChartData } from "components/dashboard/charts/ChartConfig/configLineChartData";
import { configMultiScatterChartData } from "components/dashboard/charts/ChartConfig/configMultiScatterChart";
import { configPieChartData } from "components/dashboard/charts/ChartConfig/configPieChartData";
import { configScatterChartData } from "components/dashboard/charts/ChartConfig/configScatterChart";
import RawDatasetsTable from "components/dashboard/charts/RawDatasetsTable";
import Stats from "components/dashboard/charts/Stats";
import DatasetBarChart from "components/dashboard/charts/bar-chart/DatasetBarChart";
import { DatasetDataInfoResponseArrayToPlotDataExArray } from "components/dashboard/charts/bar-chart/DatasetBarChartDataConverter";
import {
  convertDataToChartsData,
  getAllDatasetsTimestamps,
} from "components/dashboard/charts/utils";
import useRawValuesStore from "store/raw-values-store";
import { ColorPickerValueType } from "primereact/colorpicker";
import mapArraysByTimestampToSameLength from "../charts/multi-scatter-chart/MultiScatterConfig";
import { useCompanyTimezone, useDisplayTimezone } from "../../../hooks/useTimezone";

type ChartRendererProps = {
  selectedChartType: ChartType;

  isLoading: boolean;
  data: DatasetDataInfoResponse[];
  isError: boolean;

  dataPointCount: number;

  colorPallet: { key: string; value: Array<string> };

  singleAxis: { x: string; y: string[] };
  multiAxis: { x: string; y: string; color: string; size: string };
  colorScale: boolean;

  selectedDatasets: Array<BaseDatasetDTO>;
  aggregationType: AggregationType;
  datesRange: Date[] | undefined;
  selectedColor: ColorPickerValueType;

  windowWidth: number;
  windowHeight: number;

  scatterSelectedTimeOffset?: string;
  initialRowCount?: number;
};

const ChartRenderer = React.memo(
  ({
    selectedChartType,
    isLoading,
    data,
    colorPallet,
    dataPointCount,
    singleAxis,
    multiAxis,
    isError,
    selectedDatasets,
    aggregationType,
    windowHeight,
    windowWidth,
    colorScale,
    datesRange,
    selectedColor,
    scatterSelectedTimeOffset,
    initialRowCount,
  }: ChartRendererProps) => {
    const displayTz = useDisplayTimezone();
    const companyTz = useCompanyTimezone()

    const [printAllowed, setPrintAllowed] = useState(false);
    const [convertedCharData, setConvertedCharData] = useState<ChartData[]>([]);
    const showRawValues = useRawValuesStore(state => state.showRawValues);
    
    useEffect(() => {
      if (data && data.length > 0 && selectedChartType !== "MULTISCATTER") {
        setConvertedCharData(convertDataToChartsData(data));
      } else {
        setConvertedCharData([]);
      }
    }, [data]);

    useEffect(() => {
      if (selectedChartType === "MULTISCATTER" && data && data.length > 2) {
        setConvertedCharData(convertDataToChartsData(data, scatterSelectedTimeOffset));
      }
    }, [data, scatterSelectedTimeOffset]);

    useEffect(() => {
      const moreThanZeroTraces = convertedCharData.length > 0;
      setPrintAllowed(false);

      if (
        (selectedChartType === "LINE" ||
          selectedChartType === "BAR" ||
          selectedChartType === "BOX" ||
          selectedChartType === "HISTOGRAM" ||
          selectedChartType === "RAW") &&
        moreThanZeroTraces
      ) {
        setPrintAllowed(true);
      } else if ((selectedChartType === "PIE" || selectedChartType === "STATS") && data) {
        setPrintAllowed(true);
      } else if (
        selectedChartType === "SCATTER" &&
        moreThanZeroTraces &&
        singleAxis.x !== "" &&
        singleAxis.y.length > 0 &&
        getScatterConfig(convertedCharData)
      ) {
        setPrintAllowed(true);
      } else if (
        selectedChartType === "MULTISCATTER" &&
        moreThanZeroTraces &&
        multiAxis.x !== "" &&
        multiAxis.y !== "" &&
        getMultiScatterConfig(convertedCharData)
      ) {
        setPrintAllowed(true);
      }
    }, [
      convertedCharData,
      selectedChartType,
      dataPointCount,
      singleAxis.x,
      singleAxis.y,
      multiAxis.x,
      multiAxis.y,
      multiAxis.color,
      multiAxis.size,
    ]);

    const getScatterConfig = (
      chartData: ChartData[]
    ): { X: ChartData; Y: Array<ChartData> } | undefined => {
      const datasetXAxis: ChartData | undefined = chartData.find((s: any) => s.id === singleAxis.x);
      const datasetsYAxis: Array<ChartData | undefined> = singleAxis.y.map(yAxisID => {
        return chartData.find((s: any) => {
          return s.id === yAxisID;
        });
      });

      if (datasetXAxis != null && datasetsYAxis != null) {
        return {
          X: datasetXAxis,
          Y: datasetsYAxis as Array<ChartData>,
        };
      }

      return undefined;
    };

    const getMultiScatterConfig = (
      chartData: ChartData[]
    ):
      | {
          valuesToDisplay: any;
          X: ChartData;
          Y: ChartData;
          color?: ChartData;
          size?: ChartData;
        }
      | undefined => {
      const datasetXAxis: ChartData | undefined = chartData.find((s: any) => s.id === multiAxis.x);
      const datasetYAxis: ChartData | undefined = chartData.find((s: any) => s.id === multiAxis.y);
      const datasetColorAxis: ChartData | undefined = chartData.find(
        (s: any) => s.id === multiAxis.color
      );
      const datasetSizeAxis: ChartData | undefined = chartData.find(
        (s: any) => s.id === multiAxis.size
      );
      let scatterData: {
        X?: ChartData;
        Y?: ChartData;
        color?: ChartData;
        size?: ChartData;
        valuesToDisplay?: any;
      } = {};

      if (chartData.length > 2 && chartData[2].valuesToDisplay) {
        scatterData = {
          X: datasetXAxis,
          Y: datasetYAxis,
          color: datasetColorAxis,
          size: datasetSizeAxis,
          valuesToDisplay: chartData[2].valuesToDisplay ? chartData[2].valuesToDisplay : undefined,
        };
      } else {
        scatterData = {
          X: datasetXAxis,
          Y: datasetYAxis,
          color: datasetColorAxis,
          size: datasetSizeAxis,
        };
      }

      if (scatterData.X !== undefined && scatterData.Y !== undefined) {
        return scatterData as { X: ChartData; Y: ChartData; color: ChartData; size: ChartData };
      }
      return undefined;
    };

    if (isError) {
      return <div>{T("error.error-while-fetching-data")}</div>;
    }

    if (!printAllowed || selectedDatasets.length === 0) {
      return null;
    }

    if (selectedChartType === "LINE") {
      return (
        <BasicChart
          data={data}
          selectedDatasets={selectedDatasets}
          plotData={configLineChartData(convertedCharData, colorPallet.value)}
          chartProps={{
            title: selectedChartType,
            loading: isLoading,
            identifiers: [],
            hideRefresh: true,
            height: windowHeight,
            selectedChartType,
          }}
        />
      );
    }

    if (selectedChartType === "BAR") {
      return (
        <DatasetBarChart
          data={DatasetDataInfoResponseArrayToPlotDataExArray(
            data as DatasetDataInfoResponse[],
            []
          )}
          width={windowWidth}
          height={windowHeight}
        />
      );
    }

    if (selectedChartType === "BOX") {
      return (
        <BasicChart
          selectedDatasets={selectedDatasets}
          plotData={configBoxPlotData(convertedCharData, colorPallet.value)}
          chartProps={{
            title: selectedChartType,
            loading: isLoading,
            identifiers: [],
            hideRefresh: true,
            height: windowHeight,
          }}
          data={[]}
        />
      );
    }

    if (selectedChartType === "PIE") {
      return (
        <BasicChart
          selectedDatasets={selectedDatasets}
          plotData={configPieChartData(
            createPieData(data || [], aggregationType),
            colorPallet.value
          )}
          chartProps={{
            title: selectedChartType,
            loading: isLoading,
            identifiers: [],
            hideRefresh: true,
            height: windowHeight,
          }}
          data={[]}
        />
      );
    }

    if (selectedChartType === "SCATTER") {
      const checkScatterChartDataValidity = (
        x: { keyArr: Array<Date | number>; valueArr: number[] },
        y: { keyArr: Array<Date | number>; valueArr: number[] }
      ): boolean => {
        if (!x?.keyArr || !x?.valueArr) return false;

        if (Array.isArray(y)) {
          const isAnyInvalid = y.some(s => !s?.keyArr || !s?.valueArr);
          if (isAnyInvalid) return false;

          const allArrays = y.flatMap(s => [s.keyArr, s.valueArr]);
          return !checkIfArraysLengthIsSame(x.keyArr, x.valueArr, ...allArrays);
        }

        if (!y?.keyArr || !y?.valueArr) return false;

        return !checkIfArraysLengthIsSame(x.keyArr, x.valueArr, y.keyArr, y.valueArr);
      };

      let scatterData:
        | { X: { keyArr: Array<Date | number>; valueArr: number[] }; Y: any }
        | undefined = getScatterConfig(convertedCharData);
      if (!scatterData || data.length < 2) {
        return null;
      }
      if (checkScatterChartDataValidity(scatterData.X, scatterData.Y)) {
        const tempData = mapArraysByTimestampToSameLength(data as DatasetDataInfoResponse[], {
          x: data[0].identifier as string,
          y: data[1].identifier as string,
          color: "",
          size: "",
        }) as DatasetDataInfoResponse[];
        if (tempData) {
          const tempConvertedData = convertDataToChartsData(tempData);
          scatterData = getScatterConfig(tempConvertedData);
        }
      }

      return (
        <BasicChart
          selectedDatasets={selectedDatasets}
          plotData={configScatterChartData(
            scatterData.X,
            scatterData.Y,
            colorPallet.value,
            datesRange ?? [],
            selectedColor
          )}
          chartProps={{
            title: selectedChartType,
            loading: isLoading,
            identifiers: [],
            hideRefresh: true,
            height: windowHeight,
            selectedChartType,
          }}
          data={[]}
        />
      );
    }
    if (selectedChartType === "MULTISCATTER") {
      let multiScatterData = getMultiScatterConfig(convertedCharData);
      if (!multiScatterData) {
        return null;
      }

      if (
        !checkIfArraysLengthIsSame(
          multiScatterData.X?.valueArr,
          multiScatterData.Y?.valueArr,
          multiScatterData.color?.valueArr,
          multiScatterData.size?.valueArr
        )
      ) {
        const tempData = mapArraysByTimestampToSameLength(
          data as DatasetDataInfoResponse[],
          multiAxis
        ) as DatasetDataInfoResponse[];
        if (tempData) {
          const tempConvertedData = convertDataToChartsData(tempData);
          multiScatterData = getMultiScatterConfig(tempConvertedData);
        }
      }

      if (multiScatterData?.X === undefined || multiScatterData?.Y === undefined) {
        return null;
      }

      return (
        <BasicChart
          selectedDatasets={selectedDatasets}
          plotData={configMultiScatterChartData({
            xData: multiScatterData.X,
            yData: multiScatterData.Y,
            colorData: multiScatterData.color,
            sizeData: multiScatterData.size,
            selectedColorPalette: colorPallet.value,
            colorscale: colorScale,
            dataToDisplay: multiScatterData.valuesToDisplay
              ? multiScatterData.valuesToDisplay
              : undefined,
            displayTimezone: displayTz
          })}
          chartProps={{
            title: selectedChartType,
            loading: isLoading,
            identifiers: [],
            hideRefresh: true,
            height: windowHeight,
            legend: { orientation: "v" },
            xaxisName: multiScatterData.X.unit || "",
          }}
          data={[]}
        />
      );
    }

    if (selectedChartType === "HISTOGRAM") {
      return (
        <BasicChart
          selectedDatasets={selectedDatasets}
          plotData={configHistogramData(convertedCharData, colorPallet.value)}
          chartProps={{
            title: selectedChartType,
            loading: isLoading,
            identifiers: [],
            hideRefresh: true,
            height: windowHeight,
          }}
          data={[]}
        />
      );
    }

    if (selectedChartType === "STATS") {
      if (!data) {
        return null;
      }
      return <Stats statsArr={createIStatsRows(data)} />;
    }

    if (selectedChartType === "RAW") {
      if (!data) {
        return null;
      }
      return (
        <RawDatasetsTable
          data={getAllDatasetsTimestamps(data, showRawValues)}
          initialRowCount={initialRowCount}
          showRawValues={showRawValues}
        />
      );
    }

    return null;
  }
);

ChartRenderer.displayName = "ChartRenderer";

export default ChartRenderer;
